/**
 * cefUpdater — host bridge helpers for the CEF/OSR build.
 *
 * The in-app self-updater (GitHub-manifest check + pkexec .deb apply) was
 * removed: the desktop app is now distributed via an apt repo, so updates come
 * through `apt upgrade`, not an in-app button. What remains here are the two
 * small host-bridge helpers that other UI still needs:
 *
 *   - isCefHost()        — are we running inside the CEF host?
 *   - getCefAppVersion() — the running host's version (StatusFooter label).
 *
 * Both talk to the native host over the same `window.cefQuery` bridge used for
 * IPC discovery and browser control.
 */

/** True when we're running inside the CEF host (window.cefQuery is installed). */
export function isCefHost(): boolean {
  return typeof window !== 'undefined' && typeof window.cefQuery === 'function';
}

/** Send one cefQuery and resolve on the host's reply (rejects on failure). */
function hostQuery(payload: Record<string, unknown>): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    if (!isCefHost()) {
      reject(new Error('cefUpdater: not running in the CEF host'));
      return;
    }
    window.cefQuery!({
      request: JSON.stringify(payload),
      onSuccess: (response: string) => resolve(response),
      onFailure: (code: number, message: string) =>
        reject(new Error(`cefUpdater (${code}): ${message}`)),
    });
  });
}

/** The running CEF host's app version (e.g. for the status-bar label). */
export async function getCefAppVersion(): Promise<string | null> {
  if (!isCefHost()) return null;
  try {
    return await hostQuery({ type: 'app-version' });
  } catch {
    return null;
  }
}
